import { type FocusEvent, type MouseEvent, type PointerEvent, useCallback, useEffect } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { isEditorPortalTarget, isOverlayPortalTarget } from '@/lib/editor-portal-targets';
import { addWindowEventListener, elementFromEventTarget, getDocument } from '@/lib/utils';

const BLOCK_SELECTION_ID_ATTR = 'data-block-selection-id';
const BLOCK_SELECTION_SECTION_ID_ATTR = 'data-block-selection-section-id';
const BLOCK_SELECTION_SELECTOR = `[${BLOCK_SELECTION_ID_ATTR}]`;
const COLLECTION_CARD_EDITOR_SELECTOR = '[data-collection-card-editor-root]';
const COLLECTION_ITEMS_INTERACTIVE_SELECTOR = '[data-collection-items-interactive]';
const BLOCK_TEXT_SURFACE_SELECTOR = '[data-block-text-editor-root], [data-block-text-preview-root]';

export interface BlockKey {
  sectionId: string;
  blockId: string;
}

export interface BeginInlineEditOptions {
  /**
   * When true, page-level section chrome (Add Element / section toolbar / Add Section) is hidden
   * until inline edit ends. Overlays (image picker, etc.) must pass false — they reuse `inlineEdit`
   * for toolbar pinning only.
   */
  suppressPageSectionChrome?: boolean;
}

export interface BlockSelectionState {
  hovered: BlockKey | null;
  active: BlockKey | null;
  inlineEdit: BlockKey | null;
  /** True while a body text block or footer text is in rich-text inline edit (not overlay sessions). */
  suppressChromeForInlineText: boolean;
  /** Editor section UI key while a collection card text field is in rich-text edit mode. */
  collectionRichTextEditorSectionId: string | null;
  /** True while focus is inside an editor portal (popover/dialog/dropdown/select). */
  overlayFocused: boolean;
  setHovered: (ref: BlockKey | null) => void;
  setActiveBlock: (sectionId: string, blockId: string, options?: { syncHover?: boolean }) => void;
  clearActiveForSection: (sectionId: string) => void;
  setInlineEdit: (sectionId: string, blockId: string | null) => void;
  beginInlineEdit: (sectionId: string, blockId: string, options?: BeginInlineEditOptions) => void;
  setCollectionCardRichTextFocus: (active: boolean, editorSectionId?: string | null) => void;
  setOverlayFocused: (focused: boolean) => void;
  clearBlockState: () => void;
}

export interface BlockSelectionBindingProps {
  'data-block-selection-id': string;
  'data-block-selection-section-id': string;
  onPointerEnter: () => void;
  onPointerLeave: (event: PointerEvent<HTMLElement>) => void;
  onClickCapture: (event: MouseEvent<HTMLElement>) => void;
  onFocusCapture: (event: FocusEvent<HTMLElement>) => void;
  onBlurCapture: (event: FocusEvent<HTMLElement>) => void;
}

function isSameBlockKey(a: BlockKey | null, b: BlockKey | null): boolean {
  return a?.sectionId === b?.sectionId && a?.blockId === b?.blockId;
}

function isTargetBlock(ref: BlockKey | null, sectionId: string, blockId: string): boolean {
  return ref?.sectionId === sectionId && ref?.blockId === blockId;
}

function hasAnyBlockSelectionState(
  state: Pick<BlockSelectionState, 'active' | 'hovered' | 'inlineEdit'>,
) {
  return Boolean(state.active || state.inlineEdit || state.hovered);
}

function resolveBlockKeyFromElement(element: Element | null): BlockKey | null {
  if (!element) {
    return null;
  }

  const el = element.closest<HTMLElement>(BLOCK_SELECTION_SELECTOR);
  if (!el) {
    return null;
  }

  const blockId = el.getAttribute(BLOCK_SELECTION_ID_ATTR);
  const sectionId = el.getAttribute(BLOCK_SELECTION_SECTION_ID_ATTR);
  if (!blockId || !sectionId) {
    return null;
  }

  return { sectionId, blockId };
}

function resolveBlockKeyFromRelatedTarget(relatedTarget: EventTarget | null): BlockKey | null {
  return resolveBlockKeyFromElement(elementFromEventTarget(relatedTarget));
}

function isWithinBlockSelectionRoot(element: Element | null, blockId: string): boolean {
  return Boolean(element?.closest(`[data-block-selection-root="${blockId}"]`));
}

function resolveHoverFromRelatedTarget(
  relatedTarget: EventTarget | null,
  sectionId: string,
  blockId: string,
): BlockKey | null {
  if (isEditorPortalTarget(relatedTarget)) {
    return { sectionId, blockId };
  }

  const relatedElement = elementFromEventTarget(relatedTarget);
  if (isWithinBlockSelectionRoot(relatedElement, blockId)) {
    return { sectionId, blockId };
  }

  return resolveBlockKeyFromRelatedTarget(relatedTarget);
}

export const useBlockSelectionStore = create<BlockSelectionState>((set) => ({
  hovered: null,
  active: null,
  inlineEdit: null,
  suppressChromeForInlineText: false,
  collectionRichTextEditorSectionId: null,
  overlayFocused: false,
  setOverlayFocused: (focused) => {
    set((state) => (state.overlayFocused === focused ? state : { overlayFocused: focused }));
  },
  clearBlockState: () => {
    set({
      hovered: null,
      active: null,
      inlineEdit: null,
      suppressChromeForInlineText: false,
      collectionRichTextEditorSectionId: null,
    });
  },
  setHovered: (ref) => {
    set({ hovered: ref });
  },
  setActiveBlock: (sectionId, blockId, options) => {
    const nextActive: BlockKey = { sectionId, blockId };
    set((state) => {
      const keepInline = isSameBlockKey(state.inlineEdit, nextActive);
      return {
        active: nextActive,
        hovered: options?.syncHover ? nextActive : state.hovered,
        inlineEdit: keepInline ? state.inlineEdit : null,
        suppressChromeForInlineText: keepInline ? state.suppressChromeForInlineText : false,
      };
    });
  },
  clearActiveForSection: (sectionId) => {
    set((state) => {
      const clearedInline = state.inlineEdit?.sectionId === sectionId;
      return {
        active: state.active?.sectionId === sectionId ? null : state.active,
        inlineEdit: clearedInline ? null : state.inlineEdit,
        suppressChromeForInlineText: clearedInline ? false : state.suppressChromeForInlineText,
        hovered: state.hovered?.sectionId === sectionId ? null : state.hovered,
      };
    });
  },
  setInlineEdit: (sectionId, blockId) => {
    if (blockId === null) {
      set((state) => {
        const clearedInline = state.inlineEdit?.sectionId === sectionId;
        return {
          inlineEdit: clearedInline ? null : state.inlineEdit,
          suppressChromeForInlineText: clearedInline ? false : state.suppressChromeForInlineText,
        };
      });
      return;
    }

    set({ inlineEdit: { sectionId, blockId } });
  },
  beginInlineEdit: (sectionId, blockId, options) => {
    const ref: BlockKey = { sectionId, blockId };
    set({
      active: ref,
      inlineEdit: ref,
      suppressChromeForInlineText: options?.suppressPageSectionChrome === true,
    });
  },
  setCollectionCardRichTextFocus: (active, editorSectionId) => {
    set({
      collectionRichTextEditorSectionId: active && editorSectionId ? editorSectionId : null,
    });
  },
}));

function syncOverlayFocused(): void {
  const el = getDocument()?.activeElement;
  useBlockSelectionStore.getState().setOverlayFocused(Boolean(el && isOverlayPortalTarget(el)));
}

let removePointerSync: (() => void) | null = null;
function setPointerSync(on: boolean): void {
  if (on === Boolean(removePointerSync)) return;
  if (on) {
    removePointerSync =
      addWindowEventListener('pointermove', syncOverlayFocused, {
        passive: true,
        capture: true,
      }) ?? null;
  } else {
    removePointerSync?.();
    removePointerSync = null;
  }
}

function shouldIgnoreBlockSelectionEvent(target: EventTarget | null): boolean {
  const el = elementFromEventTarget(target);
  if (!el) {
    return false;
  }
  if (el.closest('[data-block-action-toolbar]')) {
    return true;
  }
  if (el.closest(COLLECTION_CARD_EDITOR_SELECTOR)) {
    return true;
  }
  if (el.closest(COLLECTION_ITEMS_INTERACTIVE_SELECTOR)) {
    return true;
  }
  if (el.closest(BLOCK_TEXT_SURFACE_SELECTOR)) {
    return true;
  }
  if (isEditorPortalTarget(el)) {
    return true;
  }

  return false;
}

export function useIsBlockHovered(sectionId: string, blockId: string) {
  return useBlockSelectionStore((state) => isTargetBlock(state.hovered, sectionId, blockId));
}

export function useIsBlockActive(sectionId: string, blockId: string) {
  return useBlockSelectionStore((state) => isTargetBlock(state.active, sectionId, blockId));
}

export function useActiveBlockId(sectionId: string): string | null | undefined {
  return useBlockSelectionStore((state) =>
    state.active?.sectionId === sectionId ? state.active.blockId : undefined,
  );
}

export function useInlineEditBlockId(sectionId: string): string | null | undefined {
  return useBlockSelectionStore((state) =>
    state.inlineEdit?.sectionId === sectionId ? state.inlineEdit.blockId : undefined,
  );
}

/**
 * Hides page-level section chrome (Add section / Add element / section toolbar) for this section
 * only while body/footer text inline edit targets it, or a collection card field is editing in it.
 */
export function usePageSectionChromeSuppressedForSection(editorSectionUiKey: string): boolean {
  return useBlockSelectionStore(
    (state) =>
      (state.suppressChromeForInlineText && state.inlineEdit?.sectionId === editorSectionUiKey) ||
      (state.collectionRichTextEditorSectionId !== null &&
        state.collectionRichTextEditorSectionId === editorSectionUiKey),
  );
}

export function useIsOverlayFocused(): boolean {
  return useBlockSelectionStore((state) => state.overlayFocused);
}

export function useIsBlockToolbarRowVisible(sectionId: string, blockId: string): boolean {
  return useBlockSelectionStore((state) => {
    const { active, inlineEdit } = state;

    if (inlineEdit?.sectionId === sectionId) {
      return inlineEdit.blockId === blockId;
    }

    return active?.sectionId === sectionId && active.blockId === blockId;
  });
}

export function useBlockSelectionActions() {
  return useBlockSelectionStore(
    useShallow((state) => ({
      setActiveBlock: state.setActiveBlock,
      setInlineEdit: state.setInlineEdit,
      beginInlineEdit: state.beginInlineEdit,
    })),
  );
}

export function useBlockSelectionDismissListeners() {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const activeElement = getDocument()?.activeElement;
      if (activeElement && isEditorPortalTarget(activeElement)) return;
      const state = useBlockSelectionStore.getState();
      if (!state.active && !state.inlineEdit) return;
      // Preserve `hovered` — Escape only dismisses selection / inline-edit.
      useBlockSelectionStore.setState({
        active: null,
        inlineEdit: null,
        suppressChromeForInlineText: false,
        collectionRichTextEditorSectionId: null,
      });
    };

    const onOutsidePointerDown = (event: Event) => {
      if (getDocument()?.body.style.pointerEvents === 'none') return;
      if (shouldIgnoreBlockSelectionEvent(event.target)) return;
      const el = elementFromEventTarget(event.target);
      if (!el || el.closest('[data-block-selection-root]')) return;
      const state = useBlockSelectionStore.getState();
      if (!hasAnyBlockSelectionState(state)) return;
      state.clearBlockState();
    };

    syncOverlayFocused();

    // Firefox/Safari don't reliably fire focusout when the focused element is
    // removed with a Radix Dialog, so overlayFocused stays true after close.
    // Attach pointermove only while an overlay is focused — first cursor move
    // after unmount re-reads activeElement and clears the flag.
    setPointerSync(useBlockSelectionStore.getState().overlayFocused);
    const unsubOverlay = useBlockSelectionStore.subscribe((s, p) => {
      if (s.overlayFocused !== p.overlayFocused) setPointerSync(s.overlayFocused);
    });

    const removeKeydown = addWindowEventListener<KeyboardEvent>('keydown', onKeyDown);
    const removePointer = addWindowEventListener('pointerdown', onOutsidePointerDown, true);
    const removeFocusIn = addWindowEventListener('focusin', syncOverlayFocused);
    const removeFocusOut = addWindowEventListener('focusout', () =>
      queueMicrotask(syncOverlayFocused),
    );
    return () => {
      removeKeydown?.();
      removePointer?.();
      removeFocusIn?.();
      removeFocusOut?.();
      unsubOverlay();
      setPointerSync(false);
    };
  }, []);
}

export function useClearActiveForSection(sectionId: string) {
  const clearActiveForSection = useBlockSelectionStore((state) => state.clearActiveForSection);
  return useCallback(() => {
    clearActiveForSection(sectionId);
  }, [clearActiveForSection, sectionId]);
}

export function useBlockSelectionBindings(
  sectionId: string,
  blockId: string,
): BlockSelectionBindingProps {
  const setHovered = useBlockSelectionStore((state) => state.setHovered);
  const setActiveBlock = useBlockSelectionStore((state) => state.setActiveBlock);

  return {
    [BLOCK_SELECTION_ID_ATTR]: blockId,
    [BLOCK_SELECTION_SECTION_ID_ATTR]: sectionId,
    onPointerEnter: () => {
      setHovered({ sectionId, blockId });
    },
    onPointerLeave: (event) => {
      if (isEditorPortalTarget(event.relatedTarget)) {
        setHovered({ sectionId, blockId });
        return;
      }
      setHovered(resolveHoverFromRelatedTarget(event.relatedTarget, sectionId, blockId));
    },
    onClickCapture: (event) => {
      const resolved = resolveBlockKeyFromElement(elementFromEventTarget(event.target));
      if (!resolved || resolved.sectionId !== sectionId || resolved.blockId !== blockId) {
        return;
      }
      if (shouldIgnoreBlockSelectionEvent(event.target)) {
        return;
      }
      setActiveBlock(sectionId, blockId);
    },
    onFocusCapture: (event) => {
      if (shouldIgnoreBlockSelectionEvent(event.target)) {
        return;
      }
      const resolved = resolveBlockKeyFromElement(elementFromEventTarget(event.target));
      if (!resolved || resolved.sectionId !== sectionId || resolved.blockId !== blockId) {
        return;
      }
      setHovered({ sectionId, blockId });
      setActiveBlock(sectionId, blockId);
    },
    onBlurCapture: (event) => {
      const relatedElement = elementFromEventTarget(event.relatedTarget);
      if (relatedElement === null) {
        const blurTarget = elementFromEventTarget(event.target);
        if (isWithinBlockSelectionRoot(blurTarget, blockId)) {
          return;
        }
      }
      setHovered(resolveHoverFromRelatedTarget(event.relatedTarget, sectionId, blockId));
    },
  };
}
